import { orderBy } from "lodash";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const KEYMAP_LABEL_ENTITY_TOKEN = new StateToken<KeymapLabelEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.KEYMAP_LABEL_ENTITY);

export type KeymapLabelEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.KeymapLabelEntityModels.KeymapLabel>;

@State<KeymapLabelEntityStateModel>({
  name: KEYMAP_LABEL_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class KeymapLabelEntityState extends HistoryEntityBaseState<FlatModels.KeymapLabelEntityModels.KeymapLabel, KeymapLabelEntityStateModel> {
  //<editor-fold desc="Selectors">
	
  @Selector([KeymapLabelEntityState])
  static override typedEntities$(state: KeymapLabelEntityStateModel): { [id: string]: FlatModels.KeymapLabelEntityModels.KeymapLabel } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
	
  @Selector([KeymapLabelEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.KeymapLabelEntityModels.KeymapLabel }): FlatModels.KeymapLabelEntityModels.KeymapLabel[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }
	
  @Selector([KeymapLabelEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.KeymapLabelEntityModels.KeymapLabel }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }
	
  @Selector([KeymapLabelEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.KeymapLabelEntityModels.KeymapLabel }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
	
  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.KeymapLabelEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<KeymapLabelEntityStateModel>,
                            action: FlatActions.KeymapLabelEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<KeymapLabelEntityStateModel>,
                              action: FlatActions.KeymapLabelEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<KeymapLabelEntityStateModel>,
                               action: FlatActions.KeymapLabelEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<KeymapLabelEntityStateModel>,
                                 action: FlatActions.KeymapLabelEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.KeymapLabelEntityActions.AddEntity)
  @Undoable(
    FlatActions.KeymapLabelEntityActions.AddEntity,
    KEYMAP_LABEL_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<KeymapLabelEntityStateModel>,
                     action: FlatActions.KeymapLabelEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.KeymapLabelEntityActions.UpdateEntity,
    KEYMAP_LABEL_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<KeymapLabelEntityStateModel>,
                        action: FlatActions.KeymapLabelEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.KeymapLabelEntityActions.RemoveEntity,
    KEYMAP_LABEL_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<KeymapLabelEntityStateModel>,
                        action: FlatActions.KeymapLabelEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.KeymapLabelEntityActions.UndoSnapshot)
	override undoSnapshot(state: StateContext<KeymapLabelEntityStateModel>,
                        action: FlatActions.KeymapLabelEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<KeymapLabelEntityStateModel>,
                        action: FlatActions.KeymapLabelEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }
	
  @Action(FlatActions.KeymapLabelEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<KeymapLabelEntityStateModel>,
                            action: FlatActions.KeymapLabelEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }
  
  @Action(FlatActions.KeymapLabelEntityActions.RearrangeKeymapLabels)
  rearrangeKeymapLabels({getState, dispatch}: StateContext<KeymapLabelEntityStateModel>,
                        {keymapLabels}: FlatActions.KeymapLabelEntityActions.RearrangeKeymapLabels) {
    // Get Entities
    const entities = structuredClone(getState().entities);
    
    // Get Keymap Label Entities
    const keymapLabelEntities: FlatModels.KeymapLabelEntityModels.KeymapLabel[] = [];
    keymapLabels.forEach((keymapLabel) => {
      // Get Entity
      let entity;
      if (typeof keymapLabel === 'string') {
        entity = entities[keymapLabel];
      } else {
        entity = entities[keymapLabel.id];
      }
      if (entity) {
        keymapLabelEntities.push(entity);
      }
    });
    
    // Iterate Through Keymap Label Entities
    let i = 0;
    for (const keymapLabel of orderBy(keymapLabelEntities, (a) => a.order || 0, 'asc')) {
      // Update Entity
      keymapLabel.order = i;
      
      // Dispatch Update
      dispatch(new FlatActions.KeymapLabelEntityActions.UpdateEntity(keymapLabel));
      
      // Increment i
      i++;
    }
  }

  //</editor-fold>

  //</editor-fold>
}
